import { ReactNode } from "react";

export interface SummaryStat {
  label: string;
  value: ReactNode;
  /** If numeric, zeros are hidden automatically to reduce visual noise. */
  hideIfZero?: boolean;
  hint?: ReactNode;
  /** Optional accessible full text when the visual value is a React node. */
  valueLabel?: string;
}

interface Props {
  stats: SummaryStat[];
}

/**
 * The Executive Summary strip. Calm horizontal row of at most 4-6 numbers.
 * Zeros are hidden by default — nothing should scream for attention that
 * doesn't deserve it.
 */
export function ExecutiveSummary({ stats }: Props) {
  const visible = stats.filter((s) => {
    if (!s.hideIfZero) return true;
    const v = s.value;
    if (typeof v === "number") return v > 0;
    if (typeof v === "string") return v.trim() !== "" && v !== "0" && v !== "$0.00" && v !== "$0";
    return true;
  });
  if (visible.length === 0) return null;

  return (
    <div className="paper-card p-4 md:p-5">
      <div className="grid gap-x-5 gap-y-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {visible.map((s) => (
          <div key={s.label} className="min-w-0 border-b border-border/50 pb-3 last:border-b-0 sm:border-b-0 sm:pb-0">
            <p className="eyebrow whitespace-normal leading-relaxed">{s.label}</p>
            <p
              className="mt-1 font-heading text-xl md:text-2xl text-foreground whitespace-normal break-words leading-tight"
              aria-label={s.valueLabel}
            >
              {s.value}
            </p>
            {s.hint && (
              <p className="text-xs text-muted-foreground whitespace-normal leading-snug mt-1">
                {s.hint}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
